import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { PostTradeService } from '../../post-trade.service';
import { Router } from '@angular/router';
import Highcharts from 'highcharts';
import {
  tradeInfoKey,
  productInfoKey,
  suitabilityKey,
  accountKey,
  concurrentTradesKey,
  tradeHistoryKey,
  tradeApprovalKey,
  tradePositionKey,
  annuityPositionKey,
  associatedCaseKey,
  productModulesKey,
  caseHistoryKey,
  breakpointAnalysisKey,
  chartOptions,
} from './trade-review-detail-keys';
import { MatSnackBar } from '@angular/material/snack-bar';
import isEmpty from 'lodash-es/isEmpty';
import { CaseManagementService } from '../../../case-management/case-management.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { MatDialog } from '@angular/material/dialog';
import { ExternalGroupingDialogComponent } from '../../account-grouping/external-grouping-dialog/external-grouping-dialog.component';
import { scoreClassKey } from '../../../lib/shared-value-keys';
import { TagsEvaluationService } from '../../../services/tags-evaluation.service';
// import { Chart } from 'angular-highcharts';
import { CaseCreationDialogComponent } from '../../components/case-creation-dialog/case-creation-dialog.component';
import {
  AiSummaryResponse,
  PostTradeDetailResponse,
} from '../../../models/post-trade-models';

@Component({
  selector: 'app-trade-review-detail',
  templateUrl: './trade-review-detail.component.html',
  styleUrls: ['./trade-review-detail.component.scss'],
  standalone: false,
})
export class TradeReviewDetailComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  fb = inject(FormBuilder);
  ptSvc = inject(PostTradeService);
  cms = inject(CaseManagementService);
  ss = inject(SessionStorageService);
  private route = inject(Router);
  private location = inject(Location);
  private tes = inject(TagsEvaluationService);
  private snacky = inject(MatSnackBar);

  @Input() tradeId;
  rights = [];
  tradeDetail;
  breakpointAnalysisKey = breakpointAnalysisKey;
  caseHistoryKey = caseHistoryKey;
  tradeInfoKey = tradeInfoKey;
  productInfoKey = productInfoKey;
  suitabilityKey = suitabilityKey;
  accountKey = accountKey;
  concurrentTradesKey = concurrentTradesKey;
  tradeHistoryKey = tradeHistoryKey;
  tradeApprovalKey = tradeApprovalKey;
  tradePositionKey = tradePositionKey;
  annuityPositionKey = annuityPositionKey;
  associatedCaseKey = associatedCaseKey;
  productModulesKey = productModulesKey;
  scoreClassKey = scoreClassKey;
  isEmpty = isEmpty;
  productRecommendations = {};
  charts = {
    breakpoints: null,
    portfolio: [],
  };
  productModules = [];
  caseHistory = [];
  reviewCase = false;
  caseChangesMade = false;
  caseData;
  aiSummary;
  selectedActionTab = 0;
  selectedHoldingTab = 0;
  chartSelection = 'PT';
  chartOptions = chartOptions;
  updateFlag = false;

  Highcharts: typeof Highcharts = Highcharts;
  unsubscribe: Subject<any> = new Subject();
  loading = false;

  ngOnInit(): void {
    this.loading = true;
    this.rights = this.ss.get('rights');
    this.ptSvc
      .getPostTradeDetail(this.tradeId)
      .subscribe((resp: PostTradeDetailResponse) => {
        if (resp['Details'] && resp['Details'].bridges) {
          resp['Details'].bridges.forEach(bridge => {
            if (bridge.trueRules) {
              bridge.trueRules.forEach(rule => {
                rule.explanation =
                  rule.reasonType === 'Negative'
                    ? this.tes.evalTags(rule.explanation)
                    : '';
                rule.headline =
                  rule.reasonType === 'Negative'
                    ? this.tes.evalTags(rule.headline)
                    : '';
                bridge.negativeReason =
                  rule.reasonType === 'Negative' ? true : bridge.negativeReason;
              });
            }
          });
        }

        this.tradeDetail = resp;
        this.sortProductRecommendations(resp.Details?.ProductSelected);
        if (resp.Details?.bridges && resp.Details?.bridges[0]) {
          this.sortCharts(resp.Details?.bridges[0]);
        }

        this.loading = false;
      });

    this.cms
      .getRelatedCases(this.tradeId, ['Trade', 'TradeGroup'], 'rb')
      .subscribe(resp => {
        if (resp['length'] > 0) {
          Object.keys(resp).map(index => {
            this.caseHistory.push(resp[index]);
          });
        }
      });

    this.ptSvc
      .getAiSummary(this.tradeId)
      .subscribe((resp: AiSummaryResponse) => {
        if (resp.result) {
          this.aiSummary = resp.result;
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  sortProductRecommendations(productList) {
    if (productList && productList.length > 0) {
      productList.map(product => {
        if (this.productRecommendations[product.Module]) {
          this.productRecommendations[product.Module].push(product);
        } else {
          this.productModules.push(product.Module);
          this.productRecommendations[product.Module] = [product];
        }
      });
    }
  }

  sortCharts(bridge) {
    if (bridge && bridge.chart) {
      const chartLabels = bridge.chart.split('|');
      chartLabels.map((label, index) => {
        if (label === 'ChartBreakpoint' && bridge.charts[index]) {
          this.charts['breakpoints'] = bridge.charts[index];
        } else {
          this.charts['portfolio'].push(bridge.charts[index]);
        }
      });
    }
  }

  nav(location, external = false) {
    if (external) {
      window.open(
        `${this.location.prepareExternalUrl(`/${location}`)}`,
        '_blank'
      );
    } else {
      this.route.navigate([`/${location}`]);
    }
  }

  toggleApproval(approvalStatus) {
    this.loading = true;
    this.ptSvc
      .updateTrade({
        TradeID: [this.tradeId],
        ApprovalStatus: approvalStatus,
      })
      .subscribe({
        next: () => {
          this.ptSvc.getPostTradeDetail(this.tradeId).subscribe(resp => {
            this.snacky.open(`${this.tradeId} was updated!`, 'Close', {
              duration: 4000,
            });

            this.tradeDetail = resp;
            this.loading = false;
          });
        },
        error: err => {
          this.loading = false;
          this.snacky.open(
            `Oops! Something went wrong, please try again later`,
            'Close',
            {
              duration: 4000,
            }
          );
          console.warn(`Error: ${err}`);
        },
      });
  }

  initiateCase() {
    const alerts = [];

    if (
      this.tradeDetail?.Details?.bridges &&
      this.tradeDetail?.Details?.bridges.length > 0
    ) {
      this.tradeDetail.Details.bridges[0].trueRules.map(alert => {
        if (alert.score < 0) {
          alerts.push(alert);
        }
      });
    }

    const confirmationDialog = this.dialog.open(CaseCreationDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        alerts: alerts,
        tradeRep: this.tradeDetail.RepCode,
      },
    });
    confirmationDialog.afterClosed().subscribe(resp => {
      if (resp?.action === 'continue') {
        this.loading = true;
        const caseData = {
          CaseManagement: {
            Type: 'Trade',
            Title: this.tradeId,
            ClientName: this.tradeDetail.ClientFullName,
            LowestScore: this.tradeDetail.ScoreClass,
          },
          CaseToProfile: {
            ProfileID: [this.tradeId],
          },
          Client_Rep: {
            RepCode: [this.tradeDetail.RepCode],
          },
        };

        if (resp?.caseAlerts.length > 0) {
          caseData['PostTradeAlerts'] = { AlertIds: resp.caseAlerts };
        }

        if (resp?.assignedTo) {
          caseData['CaseManagement']['AssignedTo'] = resp?.assignedTo;
        }

        if (resp?.repName) {
          caseData['CaseManagement']['AssignedToName'] = resp?.repName;
        }

        this.cms
          .createCaseManagementRecord(this.tradeId, caseData)
          .subscribe(resp => {
            this.loading = false;
            const note = {
              CaseRecordId: resp['result'],
              NoteText: `A case has been initiated for the selected trade.
              Please provide any necessary notes or actions.<br/>
              `,
              NoteTypeId: 3,
            };

            if (this.tradeDetail.ClientFullName) {
              note.NoteText =
                note.NoteText +
                `<br/>Client Name: ${this.tradeDetail.ClientFullName}`;
            }

            if (this.tradeDetail.AccountNumDisplay) {
              note.NoteText =
                note.NoteText +
                `<br/>Account #: ${this.tradeDetail.AccountNumDisplay}`;
            }

            if (this.tradeDetail.TradeDate) {
              note.NoteText =
                note.NoteText +
                `<br/>Trade Date: ${this.tradeDetail.TradeDate}`;
            }

            if (
              this.tradeDetail.Details.Display &&
              this.tradeDetail.Details.Display.Side
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Side: ${this.tradeDetail.Details.Display.Side}`;
            }

            if (this.tradeDetail.SecID) {
              note.NoteText =
                note.NoteText + `<br/>Security ID: ${this.tradeDetail.SecID}`;
            }

            if (
              this.tradeDetail.Details.Trade &&
              this.tradeDetail.Details.Trade.Symbol
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Symbol: ${this.tradeDetail.Details.Trade.Symbol}`;
            }

            if (
              this.tradeDetail.Details.Display &&
              this.tradeDetail.Details.Display.ProductType
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Product Name: ${this.tradeDetail.Details.Display.ProductType}`;
            }

            if (
              this.tradeDetail.Details.Trade &&
              this.tradeDetail.Details.Trade.Amount
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Amount: $${this.tradeDetail.Details.Trade.Amount}`;
            }

            note.NoteText = note.NoteText + `<br/>~${this.tradeId}`;

            this.cms.addCaseRecordNote(note).subscribe({
              next: () => {
                this.snacky.open('Case created & note saved', 'Close', {
                  duration: 2000,
                });
              },
              error: error => {
                this.snacky.open(`Error: ${error}`);
              },
            });

            this.nav(`case-management/case-manager/${resp['result']}`);
          });
      }
    });
  }

  downloadFactSheet() {
    this.loading = true;
    setTimeout(() => {
      this.loading = false;
    }, 4000);
  }

  toggleRows(rowProp, value) {
    if (this[rowProp] !== value) {
      this[rowProp] = value;
    } else {
      this[rowProp] = 5;
    }
  }

  changeCaseValue(value) {
    this.caseChangesMade = value;
  }

  accountGrouping() {
    this.dialog.open(ExternalGroupingDialogComponent, {
      data: {
        externalInitData: {
          FullName: this.tradeDetail.Details.AccountHolder.Full_Name,
          AccountID: this.tradeDetail.AccountID,
          HashedSSN: this.tradeDetail.Details.AccountHolder.HashedSSN,
          ROANumber: this.tradeDetail.ROANumber,
          CumulativeDiscountNum: this.tradeDetail.CumulativeDiscountNum,
          GroupID: this.tradeDetail.GroupID,
          Description: this.tradeDetail.GroupDescription,
        },
      },
      width: '80vw',
      maxWidth: '80vw',
      autoFocus: false,
    });
  }
}
